import { type ReactNode, useEffect, useRef, useState } from 'react'
import { defineMessages, useT } from '../i18n/index.ts'
import { cn } from '../lib/cn.ts'
import { IconButton } from './Button.tsx'
import { IconClose } from './Icon.tsx'

const messages = defineMessages({
	en: { close: 'Close' },
	ro: { close: 'Închide' },
	ru: { close: 'Закрыть' },
	fr: { close: 'Fermer' },
	it: { close: 'Chiudi' },
	kk: { close: 'Жабу' },
	es: { close: 'Cerrar' },
	pt: { close: 'Fechar' },
	de: { close: 'Schließen' },
})

/**
 * A native modal `<dialog>`: focus trap, Esc, inert page and top layer come from the browser.
 * Enters with scale .96 + fade (240ms), leaves faster (160ms): closing plays the exit, then
 * calls `close()`. Esc and a click on the backdrop ask `onClose`; the parent owns `open`.
 *
 * `placement="sheet"` turns it into a bottom sheet on phones (a centred dialog on desktop).
 */
export function Dialog({
	id,
	open,
	onClose,
	title,
	description,
	children,
	footer,
	size = 'md',
	placement = 'center',
	hideTitle = false,
	className,
}: {
	id: string
	open: boolean
	onClose: () => void
	title: ReactNode
	description?: ReactNode
	children?: ReactNode
	/** Actions row, right-aligned; the primary action last. */
	footer?: ReactNode
	size?: 'sm' | 'md' | 'lg' | 'xl'
	placement?: 'center' | 'sheet'
	/** Keep the title for screen readers only. */
	hideTitle?: boolean
	className?: string
}) {
	const t = useT(messages)
	const ref = useRef<HTMLDialogElement>(null)
	const closeRef = useRef(onClose)
	const downOnBackdrop = useRef(false)
	closeRef.current = onClose
	// Content stays mounted through the exit animation, then unmounts (timers, videos stop).
	const [shown, setShown] = useState(open)

	useEffect(() => {
		const d = ref.current
		if (!d) return
		if (open && !d.open) {
			d.removeAttribute('data-closing')
			setShown(true)
			d.showModal()
			return
		}
		if (!open && d.open) {
			const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			if (reduce) {
				d.close()
				setShown(false)
				return
			}
			d.setAttribute('data-closing', '')
			const done = (e?: Event) => {
				if (e && e.target !== d) return
				d.removeEventListener('animationend', done)
				d.removeAttribute('data-closing')
				d.close()
				setShown(false)
			}
			const timer = setTimeout(done, 200)
			d.addEventListener('animationend', done)
			return () => {
				clearTimeout(timer)
				d.removeEventListener('animationend', done)
			}
		}
	}, [open])

	// The browser focuses the first control (the X) when it opens, before the content has
	// rendered; the field the dialog exists for marks itself `data-autofocus` and takes focus here.
	useEffect(() => {
		if (open && shown) ref.current?.querySelector<HTMLElement>('[data-autofocus]')?.focus()
	}, [open, shown])

	return (
		<dialog
			ref={ref}
			id={id}
			aria-labelledby={`${id}-title`}
			aria-describedby={description ? `${id}-desc` : undefined}
			onCancel={(e) => {
				e.preventDefault()
				closeRef.current()
			}}
			// A dialog opened by code (after publishing), not a click, has no user activation, and
			// Chrome then swallows the first Esc's cancel event. Esc is handled here instead.
			onKeyDown={(e) => {
				if (e.key !== 'Escape') return
				e.preventDefault()
				closeRef.current()
			}}
			// A backdrop click starts AND ends on the dialog itself. A press on a menu (drawn inside the
			// dialog) released elsewhere reaches here with the dialog as target, and must not close it.
			onPointerDown={(e) => {
				downOnBackdrop.current = e.target === ref.current
			}}
			onClick={(e) => {
				if (e.target === ref.current && downOnBackdrop.current) closeRef.current()
				downOnBackdrop.current = false
			}}
			data-placement={placement}
			className={cn(
				'tt-dialog max-h-[calc(100dvh-2rem)] w-[min(var(--dialog-w),calc(100vw-2rem))] overflow-y-auto rounded-dialog bg-sheet p-0 text-ink shadow-3',
				size === 'sm' && '[--dialog-w:26rem]',
				size === 'md' && '[--dialog-w:34rem]',
				size === 'lg' && '[--dialog-w:46rem]',
				size === 'xl' && '[--dialog-w:60rem]',
				className,
			)}
		>
			{shown && (
				<div className="flex flex-col gap-5 p-5 sm:p-7">
					<div className="flex items-start justify-between gap-4">
						<div className={cn('flex min-w-0 flex-col gap-1', hideTitle && 'sr-only')}>
							<h2 id={`${id}-title`} className="text-h3 text-ink">
								{title}
							</h2>
							{description && (
								<p id={`${id}-desc`} className="text-body text-ink-2">
									{description}
								</p>
							)}
						</div>
						<IconButton
							id={`${id}-x`}
							label={t.close}
							icon={<IconClose />}
							size="sm"
							className="-mr-2 -mt-1 ml-auto shrink-0"
							onClick={() => closeRef.current()}
						/>
					</div>
					{children}
					{footer && <div className="flex flex-wrap justify-end gap-2 pt-1">{footer}</div>}
				</div>
			)}
		</dialog>
	)
}
